/**
 * Error message utilities
 *
 * Sanitize browser-native error strings (Safari "Load failed", Chrome "Failed to fetch")
 * and convert backend error codes to user-friendly i18n messages.
 *
 * This module is the single point of defense against raw error strings
 * leaking into the UI. All error-to-UI paths should go through these functions.
 */

import i18n from '@/i18n';
import { ERROR_CODE_NETWORK, ERROR_CODE_TIMEOUT } from '@/api/client';

// ERROR_CODE_ACCESS_DENIED anchors the wire-contract string shared with the
// Go backend (api-service-go/middleware.go errCodeAccessDenied). Using a
// const on both sides means a rename produces a compile/lint error instead
// of a silent mismatch.
export const ERROR_CODE_ACCESS_DENIED = 'ACCESS_DENIED' as const;
// ERROR_CODE_NOT_FOUND anchors the NOT_FOUND wire value for the same reason.
export const ERROR_CODE_NOT_FOUND = 'NOT_FOUND' as const;

/**
 * Browser-native error strings that must never be shown to users.
 * Maps raw error message substrings to i18n keys.
 *
 * Safari: "Load failed" (TypeError)
 * Chrome/Edge: "Failed to fetch" (TypeError)
 * Firefox: "NetworkError when attempting to fetch resource" (TypeError)
 * Abort: "The operation was aborted" / "signal is aborted" (AbortError)
 * Timeout: "The user aborted a request" (AbortError from AbortController.abort())
 */
const BROWSER_ERROR_PATTERNS: Array<{ pattern: RegExp; i18nKey: string }> = [
  { pattern: /load failed/i, i18nKey: 'chat.errors.networkError' },
  { pattern: /failed to fetch/i, i18nKey: 'chat.errors.networkError' },
  { pattern: /networkerror/i, i18nKey: 'chat.errors.networkError' },
  { pattern: /network error/i, i18nKey: 'chat.errors.networkError' },
  { pattern: /the operation was aborted/i, i18nKey: 'chat.errors.networkError' },
  { pattern: /signal is aborted/i, i18nKey: 'chat.errors.networkError' },
  { pattern: /request timed out/i, i18nKey: 'chat.errors.networkError' },
  { pattern: /timeout/i, i18nKey: 'chat.errors.networkError' },
];

/**
 * Sanitize any error message, replacing browser-native strings with i18n translations.
 * Use this for any error string that may be displayed in the UI.
 *
 * @param message - Raw error message (possibly from browser TypeError)
 * @returns User-friendly translated message
 */
export function sanitizeErrorMessage(message: string): string {
  if (!message) {
    return i18n.t('chat.errors.llmError');
  }

  for (const { pattern, i18nKey } of BROWSER_ERROR_PATTERNS) {
    if (pattern.test(message)) {
      return i18n.t(i18nKey);
    }
  }

  return message;
}

/**
 * Convert backend error codes to human-readable i18n messages.
 * Also sanitizes the fallback message to catch browser-native strings.
 */
export function getHumanReadableError(
  errorCode: string,
  fallbackMessage: string,
): string {
  switch (errorCode) {
    case "SERVICE_UNAVAILABLE_EMBEDDING":
    case "SERVICE_UNAVAILABLE_MEDICATION":
    case "SERVICE_UNAVAILABLE_SNOMED":
    case "SERVICE_CIRCUIT_OPEN":
      // Tool call failures - fallback to training knowledge
      return i18n.t('chat.errors.toolCallError');
    case "LLM_ERROR":
      return i18n.t('chat.errors.llmError');
    case "PROTOCOL_BOOTSTRAP_FAILED":
      // The LLM tried to start a clinical protocol but the runtime could
      // not load it (catalog miss, malformed JSON, missing default locale,
      // or DB failure). The corresponding lead-in text and temp message
      // are removed from the cache by useStreamingChat; here we render the
      // user-facing message so the nurse sees a clean retry surface.
      return i18n.t('chat.errors.protocolBootstrapFailed');
    case ERROR_CODE_ACCESS_DENIED:
      return i18n.t('errors.accessDenied');
    case ERROR_CODE_NOT_FOUND:
      // errors.resourceNotFound ("This resource is no longer available.") is
      // scoped to API resource absence; errors.notFound is for the router's
      // 404 page. Using the wrong key here shows routing-concept language
      // while the user is on a valid URL.
      return i18n.t('errors.resourceNotFound');
    case ERROR_CODE_TIMEOUT:
    case ERROR_CODE_NETWORK:
      return i18n.t('chat.errors.networkError');
    default:
      // Sanitize the fallback in case it contains browser-native strings
      return sanitizeErrorMessage(fallbackMessage) || i18n.t('chat.errors.llmError');
  }
}
